import { Box, Button, IconButton, Paper, Popover, Stack, Tab, Tabs, TextField, Menu, MenuItem, Divider, Alert, CircularProgress, Link } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import React from 'react';
import { Facebook, AccountCircle, Person, Delete } from '@mui/icons-material';
import { signIn, signInWithRedirect, signOut, signUp, confirmSignUp, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useAuthGen2 } from '../hooks/useAuthGen2';

const AuthGen2: React.FC = () => {
    const [username, setUsername] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [signUpEmail, setSignUpEmail] = React.useState('');
    const [signUpPassword, setSignUpPassword] = React.useState('');
    const [confirmationCode, setConfirmationCode] = React.useState('');
    const [isAwaitingConfirmation, setIsAwaitingConfirmation] = React.useState(false);
    const [signInLoading, setSignInLoading] = React.useState(false);
    const [signUpLoading, setSignUpLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');

    // Forgot Password states
    const [isForgotPasswordFlow, setIsForgotPasswordFlow] = React.useState(false);
    const [isAwaitingResetCode, setIsAwaitingResetCode] = React.useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = React.useState('');
    const [resetCode, setResetCode] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');
    const [resetPasswordLoading, setResetPasswordLoading] = React.useState(false);

    const { isAuthenticated, refreshAuth, clearAuth, loading } = useAuthGen2();
    
    const handleProviderSignIn = async (provider: 'Google' | 'Facebook') => {
      try {
        // Check if user is already authenticated - either by our state or if Amplify thinks so
        if (isAuthenticated && !loading) {
          handleLoginClose();
          return;
        }
        
        await signInWithRedirect({ provider });
        
      } catch (error) {
        console.error('OAuth sign in error:', error);
        
        // Handle potential "already authenticated" scenarios
        if (error && typeof error === 'object' && 'name' in error) {
          if ((error as any).name === 'UserAlreadyAuthenticatedException') {
            await refreshAuth();
            handleLoginClose();
          }
        }
      }
    }

    const handleSignIn = async () => {
      try {
        // Clear any previous errors
        setErrorMessage('');

        // Validate inputs
        if (!username || !password) {
          setErrorMessage('Please enter both email and password');
          return;
        }

        // Only block if definitely authenticated (not just loading)
        if (isAuthenticated && !loading) {
          handleLoginClose();
          return;
        }

        setSignInLoading(true);

        await signIn({
          username,
          password
        });
        // Refresh auth state after successful sign in
        await refreshAuth();
        setSignInLoading(false);
        handleLoginClose();
      } catch (error) {
        console.error('Email sign in error:', error);
        setSignInLoading(false);

        if (error && typeof error === 'object' && 'name' in error) {
          if ((error as any).name === 'UserAlreadyAuthenticatedException') {
            await refreshAuth();
            handleLoginClose();
            return;
          } else if ((error as any).name === 'NotAuthorizedException') {
            setErrorMessage('Incorrect email or password');
          } else if ((error as any).name === 'UserNotFoundException') {
            setErrorMessage('No account found with this email');
          } else if ((error as any).message) {
            setErrorMessage((error as any).message);
          } else {
            setErrorMessage('Sign in failed. Please try again.');
          }
        } else {
          setErrorMessage('Sign in failed. Please try again.');
        }
      }
    }

    const handleSignUp = async () => {
      try {
        // Clear any previous errors
        setErrorMessage('');

        // Validate inputs
        if (!signUpEmail || !signUpPassword) {
          setErrorMessage('Please enter both email and password');
          return;
        }

        if (isAuthenticated && !loading) {
          handleLoginClose();
          return;
        }

        setSignUpLoading(true);

        await signUp({
          username: signUpEmail,
          password: signUpPassword,
          options: {
            userAttributes: {
              email: signUpEmail
            }
          }
        });
        setSignUpLoading(false);
        setIsAwaitingConfirmation(true);
      } catch (error) {
        console.error('Email sign up error:', error);
        setSignUpLoading(false);

        if (error && typeof error === 'object' && 'name' in error) {
          if ((error as any).name === 'UsernameExistsException') {
            setErrorMessage('An account with this email already exists');
          } else if ((error as any).name === 'InvalidPasswordException') {
            setErrorMessage('Password must be 8+ characters with uppercase, lowercase, number, and symbol');
          } else if ((error as any).message) {
            setErrorMessage((error as any).message);
          } else {
            setErrorMessage('Sign up failed. Please try again.');
          }
        } else {
          setErrorMessage('Sign up failed. Please try again.');
        }
      }
    }

    const handleConfirmSignUp = async () => {
      try {
        // Clear any previous errors
        setErrorMessage('');

        // Validate inputs
        if (!confirmationCode) {
          setErrorMessage('Please enter the confirmation code');
          return;
        }

        setSignUpLoading(true);

        await confirmSignUp({
          username: signUpEmail,
          confirmationCode: confirmationCode
        });
        setIsAwaitingConfirmation(false);

        // Automatically sign in after confirmation
        await signIn({
          username: signUpEmail,
          password: signUpPassword
        });

        // Refresh auth state after successful sign in
        await refreshAuth();
        setSignUpLoading(false);
        handleLoginClose();
      } catch (error) {
        console.error('Confirmation error:', error);
        setSignUpLoading(false);

        if (error && typeof error === 'object' && 'name' in error) {
          if ((error as any).name === 'CodeMismatchException') {
            setErrorMessage('Invalid confirmation code. Please check and try again.');
          } else if ((error as any).name === 'ExpiredCodeException') {
            setErrorMessage('Confirmation code has expired. Please request a new one.');
          } else if ((error as any).message) {
            setErrorMessage((error as any).message);
          } else {
            setErrorMessage('Confirmation failed. Please try again.');
          }
        } else {
          setErrorMessage('Confirmation failed. Please try again.');
        }
      }
    }

    const handleForgotPassword = async () => {
      try {
        // Clear any previous errors and reset fields
        setErrorMessage('');
        setResetCode('');
        setNewPassword('');

        // Validate inputs
        if (!forgotPasswordEmail) {
          setErrorMessage('Please enter your email address');
          return;
        }

        setResetPasswordLoading(true);

        await resetPassword({ username: forgotPasswordEmail });

        setResetPasswordLoading(false);
        setIsAwaitingResetCode(true);
      } catch (error) {
        console.error('Reset password request error:', error);
        setResetPasswordLoading(false);

        if (error && typeof error === 'object' && 'name' in error) {
          if ((error as any).name === 'UserNotFoundException') {
            setErrorMessage('No account found with this email');
          } else if ((error as any).message) {
            setErrorMessage((error as any).message);
          } else {
            setErrorMessage('Failed to send reset code. Please try again.');
          }
        } else {
          setErrorMessage('Failed to send reset code. Please try again.');
        }
      }
    };

    const handleConfirmResetPassword = async () => {
      try {
        // Clear any previous errors
        setErrorMessage('');

        // Validate inputs
        if (!resetCode || !newPassword) {
          setErrorMessage('Please enter the reset code and new password');
          return;
        }

        setResetPasswordLoading(true);

        await confirmResetPassword({
          username: forgotPasswordEmail,
          confirmationCode: resetCode,
          newPassword: newPassword
        });

        setResetPasswordLoading(false);

        // Reset forgot password flow
        setIsForgotPasswordFlow(false);
        setIsAwaitingResetCode(false);
        setForgotPasswordEmail('');
        setResetCode('');
        setNewPassword('');

        // Show success message
        setErrorMessage(''); // Clear error first
        alert('Password reset successful! You can now sign in with your new password.');
      } catch (error) {
        console.error('Confirm reset password error:', error);
        setResetPasswordLoading(false);

        if (error && typeof error === 'object' && 'name' in error) {
          if ((error as any).name === 'CodeMismatchException') {
            setErrorMessage('Invalid reset code. Please check and try again.');
          } else if ((error as any).name === 'ExpiredCodeException') {
            setErrorMessage('Reset code has expired. Please request a new one.');
          } else if ((error as any).name === 'InvalidPasswordException') {
            setErrorMessage('Password must be 8+ characters with uppercase, lowercase, number, and symbol');
          } else if ((error as any).message) {
            setErrorMessage((error as any).message);
          } else {
            setErrorMessage('Failed to reset password. Please try again.');
          }
        } else {
          setErrorMessage('Failed to reset password. Please try again.');
        }
      }
    };

    const handleSignOut = async () => {
      try {
        // Immediately clear auth state for quick UI update
        clearAuth();
        handleUserMenuClose();
        
        // Use specific redirect URL based on environment to match OAuth configuration
        const getSignOutRedirectUrl = () => {
          const origin = window.location.origin;
          if (origin.includes('localhost:3000')) {
            return 'https://localhost:3000/';
          } else if (origin.includes('www.famousinitials.com')) {
            return 'https://www.famousinitials.com/';
          } else {
            return 'https://famousinitials.com/';
          }
        };

        const redirectUrl = getSignOutRedirectUrl();

        await signOut({
          global: true,
          oauth: {
            redirectUrl: redirectUrl
          }
        });
        
        // Double-check auth state after sign out completes
        setTimeout(async () => {
          await refreshAuth();
        }, 500);
        
      } catch (error) {
        console.error('Error signing out:', error);
        // Still try to refresh auth state even if sign out had an error
        await refreshAuth();
      }
    };

    const [value, setValue] = React.useState(0);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
      setValue(newValue);
      setErrorMessage(''); // Clear errors when switching tabs
    };

    const SignInView = () => {
      // If in forgot password flow, show forgot password view
      if (isForgotPasswordFlow) {
        return ForgotPasswordView();
      }

      return (
        <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}
          <Stack justifyContent="center" alignItems="center" direction="row" spacing={4}>
            <IconButton
              onClick={() => handleProviderSignIn("Facebook")}
              disabled={signInLoading}
            >
                <Facebook fontSize='large' />
            </IconButton>
            <IconButton
              onClick={() => handleProviderSignIn("Google")}
              disabled={signInLoading}
            >
                <GoogleIcon fontSize='large' />
            </IconButton>
          </Stack>
          <TextField
            id="email"
            label="Email"
            variant="outlined"
            onChange={(e) => (setUsername(e.target.value))}
            fullWidth
            sx={{ mt: 2 }}
            disabled={signInLoading}
            inputProps={{
              autoComplete: 'email'
            }}
          />
          <TextField
            id="password"
            label="Password"
            type="password"
            variant="outlined"
            onChange={(e) => (setPassword(e.target.value))}
            fullWidth
            sx={{ mt: 1 }}
            disabled={signInLoading}
            inputProps={{
              autoComplete: 'current-password'
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSignIn();
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setIsForgotPasswordFlow(true);
                setErrorMessage('');
              }}
              sx={{ cursor: 'pointer' }}
              disabled={signInLoading}
            >
              Forgot Password?
            </Link>
          </Box>
          <Button
            onClick={handleSignIn}
            variant="contained"
            sx={{ mt: 2 }}
            fullWidth
            disabled={signInLoading}
            startIcon={signInLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {signInLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </Box>
      );
    }

    const ForgotPasswordView = () => {
      // Show reset code entry if we're awaiting the code
      if (isAwaitingResetCode) {
        return (
          <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 2 }}>
              A reset code has been sent to {forgotPasswordEmail}. Please check your spam folder if you don't see it.
            </Alert>
            <Stack spacing={2}>
              <TextField
                id="resetCode"
                label="Reset Code"
                variant="outlined"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                fullWidth
                disabled={resetPasswordLoading}
                helperText="Check your email (and spam folder) for the reset code"
                inputProps={{
                  autoComplete: 'off'
                }}
              />
              <TextField
                id="newPassword"
                label="New Password"
                type="password"
                variant="outlined"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                disabled={resetPasswordLoading}
                helperText="Must be 8+ chars with uppercase, lowercase, number, and symbol"
                inputProps={{
                  autoComplete: 'new-password'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmResetPassword();
                  }
                }}
              />
              <Button
                onClick={handleConfirmResetPassword}
                variant="contained"
                disabled={resetPasswordLoading}
                startIcon={resetPasswordLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
              <Button
                onClick={() => {
                  setIsAwaitingResetCode(false);
                  setErrorMessage('');
                }}
                variant="text"
                size="small"
                disabled={resetPasswordLoading}
              >
                Back
              </Button>
            </Stack>
          </Box>
        );
      }

      // Show email entry to request reset code
      return (
        <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter your email address and we'll send you a code to reset your password.
          </Alert>
          <Stack spacing={2}>
            <TextField
              id="forgotPasswordEmail"
              label="Email"
              variant="outlined"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              fullWidth
              disabled={resetPasswordLoading}
              inputProps={{
                autoComplete: 'email'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleForgotPassword();
                }
              }}
            />
            <Button
              onClick={handleForgotPassword}
              variant="contained"
              disabled={resetPasswordLoading}
              startIcon={resetPasswordLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {resetPasswordLoading ? 'Sending Code...' : 'Send Reset Code'}
            </Button>
            <Button
              onClick={() => {
                setIsForgotPasswordFlow(false);
                setErrorMessage('');
              }}
              variant="text"
              size="small"
              disabled={resetPasswordLoading}
            >
              Back to Sign In
            </Button>
          </Stack>
        </Box>
      );
    };

    const SignUpView = () => {
      if (isAwaitingConfirmation) {
        return (
          <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 2 }}>
              A confirmation code has been sent to your email. Please check your spam folder if you don't see it.
            </Alert>
            <Stack spacing={2}>
              <TextField
                id="confirmationCode"
                label="Confirmation Code"
                variant="outlined"
                onChange={(e) => setConfirmationCode(e.target.value)}
                fullWidth
                helperText="Check your email (and spam folder) for the confirmation code"
                disabled={signUpLoading}
              />
              <Button
                onClick={handleConfirmSignUp}
                variant="contained"
                disabled={signUpLoading}
                startIcon={signUpLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {signUpLoading ? 'Confirming...' : 'Confirm Sign Up'}
              </Button>
              <Button
                onClick={() => {
                  setIsAwaitingConfirmation(false);
                  setErrorMessage('');
                }}
                variant="text"
                size="small"
                disabled={signUpLoading}
              >
                Back to Sign Up
              </Button>
            </Stack>
          </Box>
        );
      }

      return (
        <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}
          <Stack justifyContent="center" alignItems="center" direction="row" spacing={4}>
            <IconButton
              onClick={() => handleProviderSignIn("Facebook")}
              disabled={signUpLoading}
            >
              <Facebook fontSize='large' />
            </IconButton>
            <IconButton
              onClick={() => handleProviderSignIn("Google")}
              disabled={signUpLoading}
            >
              <GoogleIcon fontSize='large' />
            </IconButton>
          </Stack>
          <TextField
            id="signupEmail"
            label="Email"
            variant="outlined"
            onChange={(e) => setSignUpEmail(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            disabled={signUpLoading}
            inputProps={{
              autoComplete: 'email'
            }}
          />
          <TextField
            id="signupPassword"
            label="Password"
            type="password"
            variant="outlined"
            onChange={(e) => setSignUpPassword(e.target.value)}
            fullWidth
            helperText="Must be 8+ chars with uppercase, lowercase, number, and symbol"
            sx={{ mt: 1 }}
            disabled={signUpLoading}
            inputProps={{
              autoComplete: 'new-password'
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSignUp();
              }
            }}
          />
          <Button
            onClick={handleSignUp}
            variant="contained"
            sx={{ mt: 2 }}
            disabled={signUpLoading}
            startIcon={signUpLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {signUpLoading ? 'Signing Up...' : 'Sign Up'}
          </Button>
        </Box>
      );
    }

    const [loginOpen, setLoginOpen] = React.useState(false);
    const [loginAnchorEl, setLoginAnchorEl] = React.useState<null | HTMLElement>(null);
    const [userMenuOpen, setUserMenuOpen] = React.useState(false);
    const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleLoginClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setLoginAnchorEl(event.currentTarget);
      setLoginOpen(true);
      setErrorMessage(''); // Clear any previous errors
    };

    const handleLoginClose = () => {
      setLoginAnchorEl(null);
      setLoginOpen(false);
      setErrorMessage(''); // Clear errors on close
      setSignInLoading(false); // Reset loading states
      setSignUpLoading(false);
      setResetPasswordLoading(false);
      // Reset forgot password flow
      setIsForgotPasswordFlow(false);
      setIsAwaitingResetCode(false);
      setForgotPasswordEmail('');
      setResetCode('');
      setNewPassword('');
    };

    const handleUserMenuClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setUserMenuAnchorEl(event.currentTarget);
      setUserMenuOpen(true);
    };

    const handleUserMenuClose = () => {
      setUserMenuAnchorEl(null);
      setUserMenuOpen(false);
    };

    return (
      <>
        {
          loading ? (
            <Button sx={{marginLeft: "auto"}} color="inherit" disabled>
              Loading...
            </Button>
          ) : isAuthenticated ? (
            <IconButton 
              sx={{ marginLeft: "auto" }} 
              color="inherit" 
              onClick={handleUserMenuClick}
              aria-label="user menu"
            >
              <AccountCircle />
            </IconButton>
          ) : (
            <Button sx={{marginLeft: "auto"}} color="inherit" onClick={handleLoginClick}>Login / Register</Button>
          )
        }
        
        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchorEl}
          open={userMenuOpen}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem 
            onClick={() => {
              handleUserMenuClose();
              window.location.href = '/profile';
            }}
          >
            <Person sx={{ mr: 1 }} />
            Profile
          </MenuItem>
          <Divider />
          <MenuItem 
            onClick={() => {
              handleUserMenuClose();
              window.location.href = '/delete';
            }}
            sx={{ color: 'error.main' }}
          >
            <Delete sx={{ mr: 1 }} />
            Delete My Data
          </MenuItem>
          <Divider />
          <MenuItem 
            onClick={() => {
              handleUserMenuClose();
              handleSignOut();
            }}
          >
            Sign Out
          </MenuItem>
        </Menu>

        {/* Login/Register Popover */}
        <Popover
          open={loginOpen}
          anchorEl={loginAnchorEl}
          onClose={handleLoginClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Paper elevation={1} sx={{ width: 350, maxWidth: '90vw' }}>
            <Tabs sx={{ p:1 }} value={value} onChange={handleChange} variant="fullWidth">
              <Tab label="Sign In"/>
              <Tab label="Sign Up"/>            
            </Tabs>
            {value === 0 && SignInView()}
            {value === 1 && SignUpView()}
          </Paper>
        </Popover>
      </>
    );
};

export default AuthGen2;